// TelecontrolTab.jsx - 控制器配置Tab组件
import React, { useState, useEffect, useRef } from 'react';
import {
  Table, Card, Button, Tag, Popconfirm, Modal, Form, Select, InputNumber,
  Input, Checkbox, Space, message, Divider, Alert, Switch, Row, Col, Spin, Tabs, DatePicker
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SyncOutlined, ThunderboltOutlined,
  SettingOutlined, SearchOutlined, BellOutlined, DashboardOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const COLORS = {
  primary: '#00d4ff', text: '#ffffff', muted: 'rgba(255,255,255,0.6)',
  solar: '#f4c542', storage: '#52e8b0', load: '#ff5a7f', grid: '#3a8dde'
};
const cardStyle = { background: 'transparent', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' };
const fmtTime = t => t ? new Date(t).toLocaleString() : '-';

const TelecontrolTab = ({ controllers, subDevices, api, lz4Compress, setLz4Compress, refreshKey, currentUser }) => {
  const { t } = useTranslation();
  const isAdmin = currentUser && currentUser.isadmin !== 0;
  const [form] = Form.useForm();

  // 控制器列表：后端已按用户权限过滤
  const accessibleControllers = controllers;

  // ========== Schedule state ==========
  const [schedules, setSchedules] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [schedulePublishLoading, setSchedulePublishLoading] = useState(false);
  const [scheduleShowLatest, setScheduleShowLatest] = useState(true);
  const [schedulePageSize] = useState(5);

  // ========== Smart Dispatch state ==========
  const [smartDispatchModalOpen, setSmartDispatchModalOpen] = useState(false);
  const [smartDispatchLoading, setSmartDispatchLoading] = useState(false);
  const [smartDispatchStatus, setSmartDispatchStatus] = useState(null);

  // ========== Protection Params state ==========
  const [protectionModalOpen, setProtectionModalOpen] = useState(false);
  const [protectionLoading, setProtectionLoading] = useState(false);
  const [protectionParams, setProtectionParams] = useState([]);
  const [protectionShowLatest, setProtectionShowLatest] = useState(true);
  const [protectionPageSize] = useState(5);

  // ========== Fetch Protection Params ==========
  const fetchProtectionParams = async () => {
    setProtectionLoading(true);
    try {
      const json = await api.getProtectionParams();
      if (json.success) setProtectionParams(json.data || []);
    } catch (e) { console.error(e); }
    setProtectionLoading(false);
  };

  useEffect(() => { fetchProtectionParams(); }, []);

  // ========== Telecontrol state ==========
  const [telecontrolModalOpen, setTelecontrolModalOpen] = useState(false);
  const [telecontrolLoading, setTelecontrolLoading] = useState(false);

  // ========== Sub Telecontrol state ==========
  const [subTelecontrolModalOpen, setSubTelecontrolModalOpen] = useState(false);
  const [subTelecontrolLoading, setSubTelecontrolLoading] = useState(false);

  // ========== Device Control state ==========
  const [deviceControlModalOpen, setDeviceControlModalOpen] = useState(false);
  const [deviceControlLoading, setDeviceControlLoading] = useState(false);
  const [deviceControlKey, setDeviceControlKey] = useState(0);

  // ========== Control state ==========
  const [controlModalOpen, setControlModalOpen] = useState(false);
  const [controlLoading, setControlLoading] = useState(false);

  // ========== Device Config state ==========
  const [deviceConfigModalOpen, setDeviceConfigModalOpen] = useState(false);
  const [deviceConfigLoading, setDeviceConfigLoading] = useState(false);
  const [deviceConfigList, setDeviceConfigList] = useState([]);
  const [deviceConfigKey, setDeviceConfigKey] = useState(0);

  const fetchDeviceConfig = async () => {
    try {
      const json = await api.getDeviceConfig();
      if (json.success) setDeviceConfigList(json.data || []);
    } catch (e) { console.error(e); }
  };

  const handleDeviceConfig = async (values) => {
    try {
      setDeviceConfigLoading(true);
      let configObj = {};
      try { configObj = JSON.parse(values.config_json || '{}'); } catch { message.error('配置格式错误'); setDeviceConfigLoading(false); return; }
      const res = await api.publishDeviceConfig({
        device_info_id: values.device_info_id,
        operation: values.operation || 'replace',
        base_config_hash: null,
        device_config: configObj
      });
      if (res.success) {
        api.addLogConfig({ func: 'device_config', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('设备配置下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'device_config');
        setDeviceConfigModalOpen(false);
        fetchDeviceConfig();
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setDeviceConfigLoading(false);
  };

  useEffect(() => { fetchDeviceConfig(); }, []);

  // ========== Communication Config state ==========
  const [communicationConfigModalOpen, setCommunicationConfigModalOpen] = useState(false);
  const [communicationConfigLoading, setCommunicationConfigLoading] = useState(false);
  const [communicationConfigList, setCommunicationConfigList] = useState([]);
  const [communicationConfigKey, setCommunicationConfigKey] = useState(0);

  const fetchCommunicationConfig = async () => {
    try {
      const json = await api.getCommunicationConfig();
      if (json.success) setCommunicationConfigList(json.data || []);
    } catch (e) { console.error(e); }
  };

  const handleCommunicationConfig = async (values) => {
    try {
      setCommunicationConfigLoading(true);
      let configObj = {};
      try { configObj = JSON.parse(values.config_json || '{}'); } catch { message.error('配置格式错误'); setCommunicationConfigLoading(false); return; }
      const res = await api.publishCommunicationConfig({
        device_info_id: values.device_info_id,
        operation: values.operation || 'replace',
        base_config_hash: null,
        communication_config: configObj
      });
      if (res.success) {
        api.addLogConfig({ func: 'communication_config', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('通信配置下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'communication_config');
        setCommunicationConfigModalOpen(false);
        fetchCommunicationConfig();
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setCommunicationConfigLoading(false);
  };

  useEffect(() => { fetchCommunicationConfig(); }, []);

  // ========== Get Device Info state ==========
  const [getDeviceInfoModalOpen, setGetDeviceInfoModalOpen] = useState(false);
  const [getDeviceInfoLoading, setGetDeviceInfoLoading] = useState(false);

  // ========== Get Telemetry state ==========
  const [getTelemetryModalOpen, setGetTelemetryModalOpen] = useState(false);
  const [getTelemetryLoading, setGetTelemetryLoading] = useState(false);

  // ========== Get Alarm state ==========
  const [getAlarmModalOpen, setGetAlarmModalOpen] = useState(false);
  const [getAlarmLoading, setGetAlarmLoading] = useState(false);

  // ========== Dashboard Ctrl state ==========
  const [dashboardCtrlModalOpen, setDashboardCtrlModalOpen] = useState(false);
  const [dashboardCtrlLoading, setDashboardCtrlLoading] = useState(false);

  // ========== Fetch schedules ==========
  const fetchSchedules = async () => {
    setScheduleLoading(true);
    try {
      const json = await api.getScheduleList();
      console.log('[schedule] api response:', json);
      if (json.success) {
        console.log('[schedule] data:', json.data);
        setSchedules(json.data || []);
      }
    } catch (e) { console.error(e); }
    setScheduleLoading(false);
  };

  useEffect(() => { fetchSchedules(); }, []);

  // 监听外部刷新
  useEffect(() => {
    if (refreshKey > 0) {
      fetchSchedules();
      fetchProtectionParams();
    }
  }, [refreshKey]);

  // 按设备过滤保护参数只保留最新一条
  const latestProtectionParams = protectionShowLatest
    ? Object.values(
        protectionParams.reduce((acc, p) => {
          const key = p.device_info_id;
          if (!acc[key] || new Date(p.updated_at) > new Date(acc[key].updated_at)) acc[key] = p;
          return acc;
        }, {})
      )
    : protectionParams;

  // 轮询 ACK 状态工具函数
  const pollCommandAck = (messageId, funcName) => {
    const key = `${messageId}_${funcName}`;
    const timer = setInterval(async () => {
      const json = await api.getCommandAckStatus(messageId);
      if (json.success && json.data && json.data.status !== 'pending') {
        clearInterval(timer);
        clearTimeout(window[`_ack_timeout_${key}`]);
        const status = json.data.status;
        if (status === 'acked' || status === 'ok') {
          message.success(`${funcName} 指令已执行`);
        } else {
          message.error(`${funcName} 指令执行失败: ${json.data.error_code || json.data.message || status}`);
        }
      }
    }, 3000);
    window[`_ack_timeout_${key}`] = setTimeout(() => {
      clearInterval(timer);
      message.error('设备无响应');
    }, 60000);
  };

  // 按设备过滤只保留最新一条
  const latestSchedules = scheduleShowLatest
    ? Object.values(
        schedules.reduce((acc, s) => {
          const key = s.device_info_id;
          if (!acc[key] || new Date(s.updated_at) > new Date(acc[key].updated_at)) acc[key] = s;
          return acc;
        }, {})
      )
    : schedules;

  // ========== Handlers ==========
  const handleSchedulePublish = async () => {
    try {
      const vals = await form.validateFields();
      const c = controllers.find(c => c.id === vals.device_info_id);
      if (!c) { message.error(t('cloud.noControllerSelected')); return; }
      if (!vals.schedules || vals.schedules.length === 0) { message.error('请至少添加一个时段'); setSchedulePublishLoading(false); return; }
      if (vals.schedules.length > 10) { message.error('最多10个时段'); setSchedulePublishLoading(false); return; }
      setSchedulePublishLoading(true);
      const strategy = {
        enabled: vals.enabled ?? true,
        strategy_type: vals.strategy_type ?? 0,
        mode: vals.mode || ['peak_valley', 'dynamic_capacity', 'manual', 'third_party'][vals.strategy_type ?? 0],
        tou_loop_type: vals.tou_loop_type ?? 2,
        timezone: 'Asia/Shanghai',
        periods: vals.schedules.map(s => ({
          start: s.StartTime,
          end: s.EndTime,
          action: s.action || 'idle',
          power_kw: parseFloat(s.Power) || 0,
          soc_limit: parseFloat(s.SocLimit) ?? 100
        }))
      };
      const res = await api.publishScheduleV2({
        device_info_id: vals.device_info_id,
        strategy
      });
      if (res.success) {
        api.addLogConfig({ func: 'schedule', target: c.sn, data: { device_info_id: vals.device_info_id, strategy } });
        message.success(t('cloud.strategyPublishSuccess'));
        if (res.message_id) pollCommandAck(res.message_id, 'schedule');
        setScheduleModalOpen(false);
        fetchSchedules();
      } else {
        message.error((res.message || '') + (res.error || ''));
      }
    } catch (e) { message.error(e.message || t('cloud.strategyPublishFailed')); }
    setSchedulePublishLoading(false);
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await api.deleteSchedule(id);
      message.success(t('cloud.deleteSuccess'));
      fetchSchedules();
    } catch (e) { message.error(t('cloud.deleteFailed')); }
  };

  const handleSmartDispatch = async (values) => {
    try {
      setSmartDispatchLoading(true);
      const fmtTs = d => d ? `${d.format('YYYY-MM-DDTHH:mm:ss')}+08:00` : null;
      let payload = { device_info_id: values.device_info_id, action: values.action };
      if (values.action === 'set_enabled') {
        payload = { ...payload, enabled: values.enabled, timeout_action: 'manual_tou' };
      } else if (values.action === 'setpoint') {
        payload = { ...payload, plan_id: values.plan_id || 'default', revision: values.revision || 1, sequence: values.sequence || 1, power_kw: values.power_kw, valid_from: fmtTs(values.valid_from), valid_until: fmtTs(values.valid_until) };
      } else if (values.action === 'set_schedule') {
        const points = (values.points || []).map(p => ({
          sequence: Number(p.sequence),
          starts_at: fmtTs(p.starts_at),
          ends_at: fmtTs(p.ends_at),
          power_kw: Number(p.power_kw)
        }));
        payload = {
          ...payload,
          plan_id: values.plan_id,
          revision: Number(values.revision) || 1,
          interval_minutes: Number(values.interval_minutes) || 15,
          valid_from: fmtTs(values.valid_from),
          valid_until: fmtTs(values.valid_until),
          timeout_action: 'manual_tou',
          points
        };
      }
      const res = await api.publishSmartDispatch(payload);
      if (res.success) {
        api.addLogConfig({ func: 'smart_dispatch', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success(res.message || '下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'smart_dispatch');
        setSmartDispatchModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setSmartDispatchLoading(false);
  };

  const handleProtectionParams = async (values) => {
    try {
      setProtectionLoading(true);
      const res = await api.publishProtectionParams({
        device_info_id: values.device_info_id,
        params: {
          max_charge_power_kw: values.max_charge_power_kw,
          max_discharge_power_kw: values.max_discharge_power_kw,
          min_soc: values.min_soc,
          max_soc: values.max_soc,
          grid_export_limit_kw: values.grid_export_limit_kw,
          anti_backflow_enabled: values.anti_backflow_enabled
        }
      });
      if (res.success) {
        api.addLogConfig({ func: 'protection_params', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('保护参数下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'protection_params');
        setProtectionModalOpen(false);
        fetchProtectionParams();
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setProtectionLoading(false);
  };

  const handleTelecontrol = async (values) => {
    try {
      setTelecontrolLoading(true);
      const commands = (values.commands || []).filter(c => c.point && c.value !== undefined && c.value !== '').map(c => {
        const v = c.value;
        // 如果是纯数字转为数值类型，否则保留字符串
        const num = Number(v);
        return { point: c.point, value: !isNaN(num) && String(v).trim() !== '' ? num : v };
      });
      if (commands.length === 0) { message.error('请至少填写一个控制点位'); setTelecontrolLoading(false); return; }
      const res = await api.publishTelecontrolV2({ device_info_id: values.device_info_id, commands });
      if (res.success) {
        api.addLogConfig({ func: 'telecontrol', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: { device_info_id: values.device_info_id, commands } });
        message.success('遥控点位下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'telecontrol');
        setTelecontrolModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setTelecontrolLoading(false);
  };

  const handleSubTelecontrol = async (values) => {
    try {
      setSubTelecontrolLoading(true);
      const commands = (values.commands || []).filter(c => c.point && c.value !== undefined && c.value !== '').map(c => {
        const v = c.value;
        const num = Number(v);
        return { point: c.point, value: !isNaN(num) && String(v).trim() !== '' ? num : v };
      });
      if (commands.length === 0) { message.error('请填写至少一个点位'); setSubTelecontrolLoading(false); return; }
      const res = await api.publishSubTelecontrolV2({
        device_info_id: values.device_info_id,
        device_id: values.device_id,
        commands
      });
      if (res.success) {
        api.addLogConfig({ func: 'sub_telecontrol', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('南向设备遥控下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'sub_telecontrol');
        setSubTelecontrolModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setSubTelecontrolLoading(false);
  };

  const handleDeviceControl = async (values) => {
    try {
      setDeviceControlLoading(true);
      const points = [];
      if (values.gridModeCtrl !== undefined) points.push({ tag: 'GridModeCtrl', value: Number(values.gridModeCtrl) });
      if (values.dgOnOffCtrlMode !== undefined) points.push({ tag: 'DGOnOffCtrlMode', value: Number(values.dgOnOffCtrlMode) });
      if (values.pccOpenCloseCtrl !== undefined) points.push({ tag: 'PCCOpenCloseCtrl', value: Number(values.pccOpenCloseCtrl) });
      if (values.dgOpenCloseCtrl !== undefined) points.push({ tag: 'DGOpenCloseCtrl', value: Number(values.dgOpenCloseCtrl) });
      if (values.criticalLoadOpenCloseCtrl !== undefined) points.push({ tag: 'CriticalLoadOpenCloseCtrl', value: Number(values.criticalLoadOpenCloseCtrl) });
      if (values.normalLoadOpenCloseCtrl !== undefined) points.push({ tag: 'NormalLoadOpenCloseCtrl', value: Number(values.normalLoadOpenCloseCtrl) });

      if (points.length === 0) {
        message.error('请至少选择一个点位');
        setDeviceControlLoading(false);
        return;
      }

      const res = await api.publishDeviceControl({
        device_info_id: values.device_info_id,
        command: 'micro_grid_control',
        target_global_no: 'MICRO_GRID',
        target_dev_no: 'MICRO_GRID',
        operation: 'control',
        params: { operation: 'control', points },
        remark: values.remark || ''
      });
      if (res.success) {
        api.addLogConfig({ func: 'device_control', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('微网设备控制下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'device_control');
        setDeviceControlModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setDeviceControlLoading(false);
  };

  const handleControl = async (values) => {
    try {
      setControlLoading(true);
      const res = await api.publishControlV2({
        device_info_id: values.device_info_id,
        command: values.command,
        params: values.params ? { device_id: values.device_id, ...values.params } : { device_id: values.device_id }
      });
      if (res.success) {
        api.addLogConfig({ func: 'control', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('通用控制下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'control');
        setControlModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setControlLoading(false);
  };

  const handleGetDeviceInfo = async (values) => {
    try {
      setGetDeviceInfoLoading(true);
      const res = await api.publishGetDeviceInfo({ device_info_id: values.device_info_id });
      if (res.success) {
        api.addLogConfig({ func: 'get_device_info', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('设备清单召测已下发');
        setGetDeviceInfoModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setGetDeviceInfoLoading(false);
  };

  const handleGetTelemetry = async (values) => {
    try {
      setGetTelemetryLoading(true);
      const deviceIds = typeof values.device_ids === 'string'
        ? values.device_ids.split(',').map(s => s.trim()).filter(Boolean)
        : (values.device_ids || []);
      const res = await api.publishGetTelemetry({
        device_info_id: values.device_info_id,
        profile: values.profile || 'detail',
        device_ids: deviceIds
      });
      if (res.success) {
        api.addLogConfig({ func: 'get_telemetry', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('遥测数据召测已下发');
        setGetTelemetryModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setGetTelemetryLoading(false);
  };

  const handleGetAlarm = async (values) => {
    try {
      setGetAlarmLoading(true);
      const res = await api.publishGetAlarm({
        device_info_id: values.device_info_id,
        status: values.status || 'active',
        limit: values.limit || 100
      });
      if (res.success) {
        api.addLogConfig({ func: 'get_alarm', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('告警召测已下发');
        setGetAlarmModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setGetAlarmLoading(false);
  };

  const handleDashboardCtrl = async (values) => {
    try {
      setDashboardCtrlLoading(true);
      const res = await api.publishDashboardCtrlV2({
        device_info_id: values.device_info_id,
        enabled: values.enabled,
        interval_s: values.interval_s || 10,
        profile: values.profile || 'summary',
        ttl_s: values.ttl_s || 600
      });
      if (res.success) {
        api.addLogConfig({ func: 'dashboard_ctrl', target: controllers.find(c => c.id === values.device_info_id)?.sn || '', data: values });
        message.success('大屏推送控制下发成功');
        if (res.message_id) pollCommandAck(res.message_id, 'dashboard_ctrl');
        setDashboardCtrlModalOpen(false);
      } else {
        message.error(res.message || '下发失败');
      }
    } catch (e) { message.error(e.message); }
    setDashboardCtrlLoading(false);
  };

  const openScheduleModal = () => {
    form.setFieldsValue({
      device_info_id: accessibleControllers[0]?.id,
      enabled: true,
      tou_loop_type: 2,
      schedules: [{ StartTime: '00:00', EndTime: '06:00', action: 'charge', Power: 100, SocLimit: 90 }]
    });
    setScheduleModalOpen(true);
  };

  const openTelecontrolModal = () => {
    form.setFieldsValue({
      device_info_id: accessibleControllers[0]?.id,
      commands: [{ point: 'work_mode', value: 'auto' }]
    });
    setTelecontrolModalOpen(true);
  };

  // ========== Render ==========
  return (
    <div style={{ padding: '16px' }}>
      {/* ===== 区块A: 策略配置 ===== */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: COLORS.primary, fontSize: 13, fontWeight: 600, marginBottom: 8, borderLeft: '3px solid #00d4ff', paddingLeft: 8 }}>{t('cloud.strategyConfig')}</div>
        <Row gutter={[12, 12]}>
          {/* TOU策略下发 - 占满整行 */}
          <Col xs={24}>
            <div style={{ ...cardStyle, padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>TOU策略下发</div>
                  <Switch size="small" checked={scheduleShowLatest} onChange={setScheduleShowLatest} />
                  <span style={{ fontSize: 11, color: COLORS.muted }}>只显示最新</span>
                  <Button type="text" size="small" icon={<SyncOutlined />} onClick={fetchSchedules} style={{ color: COLORS.muted, padding: '0 4px' }} />
                </div>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openScheduleModal}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.publishStrategy')}
                </Button>
              </div>
              {scheduleLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={scheduleShowLatest ? false : { pageSize: schedulePageSize }}
                  dataSource={latestSchedules}
                  columns={[
                    { title: 'SN', dataIndex: 'sn', key: 'sn', width: 130, render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span> },
                    { title: t('cloud.stationName') || '站点', dataIndex: 'station_name', key: 'station_name', width: 120, render: v => v || '-' },
                    { title: 'source', dataIndex: 'source', key: 'source', width: 80, render: v => <Tag color={v === 'cloud' ? 'blue' : 'green'}>{v || '-'}</Tag> },
                    { title: t('cloud.enabled') || '启用', dataIndex: 'enabled', key: 'enabled', width: 70, render: v => <Tag color={v ? 'green' : 'default'}>{v ? '是' : '否'}</Tag> },
                    { title: 'strategy_type', dataIndex: 'strategy_type', key: 'strategy_type', width: 100, render: v => v ?? '-' },
                    { title: 'strategy_mode', dataIndex: 'strategy_mode', key: 'strategy_mode', width: 120, render: v => v || '-' },
                    { title: 'mode', dataIndex: 'mode', key: 'mode', width: 120, render: v => v || '-' },
                    {
                      title: t('cloud.touLoopType') || '循环类型',
                      dataIndex: 'tou_loop_type',
                      key: 'tou_loop_type',
                      width: 90,
                      render: v => [t('cloud.touLoopType0'), t('cloud.touLoopType1'), t('cloud.touLoopType2')][v] ?? '-'
                    },
                    { title: 'timezone', dataIndex: 'timezone', key: 'timezone', width: 120, render: v => v || '-' },
                    {
                      title: t('cloud.timeSlot') || '时段',
                      dataIndex: 'schedule_json',
                      key: 'periods',
                      render: (val) => {
                        let periods = Array.isArray(val) ? val : (typeof val === 'string' ? JSON.parse(val || '[]') : []);
                        if (periods.length === 0) return <span style={{ color: COLORS.muted }}>-</span>;
                        return periods.map((p, i) => (
                          <Tag key={i} style={{ marginBottom: 2, marginRight: 4 }}>
                            {p.start}~{p.end} {p.action || p.mode} {p.power_kw ?? p.power}kW{p.soc_limit != null ? ` SOC≤${p.soc_limit}%` : ''}
                          </Tag>
                        ));
                      }
                    },
                    { title: t('cloud.updateTime') || '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160, render: v => v ? new Date(v).toLocaleString() : '-' },
                  ]}
                  style={{ background: 'transparent' }}
                  scroll={{ x: 1200 }}
                />
              )}
            </div>
          </Col>

          {/* 智能调度 */}
          <Col xs={24} md={12} lg={8}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.smartDispatch') || '智能调度'}</div>
                <Button type="primary" size="small" icon={<ThunderboltOutlined />} onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id, action: 'set_enabled', enabled: true }); setSmartDispatchModalOpen(true); }}
                  style={{ background: 'rgba(244,197,66,0.15)', color: COLORS.solar, border: '1px solid rgba(244,197,66,0.3)' }}>
                  {t('cloud.telecontrol') || '下发'}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 12 }}>
                <div>{t('cloud.smartDispatchDesc') || '设置智能调度开关、目标功率和滚动计划'}</div>
              </div>
            </div>
          </Col>

          {/* 保护参数 - 占满整行 */}
          <Col xs={24}>
            <div style={{ ...cardStyle, padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.protectionParams') || '保护参数'}</div>
                  <Switch size="small" checked={protectionShowLatest} onChange={setProtectionShowLatest} />
                  <span style={{ fontSize: 11, color: COLORS.muted }}>只显示最新</span>
                  <Button type="text" size="small" icon={<SyncOutlined />} onClick={fetchProtectionParams} style={{ color: COLORS.muted, padding: '0 4px' }} />
                </div>
                <Button type="primary" size="small" icon={<SettingOutlined />} onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id }); setProtectionModalOpen(true); }}
                  style={{ background: 'rgba(82,232,176,0.15)', color: COLORS.storage, border: '1px solid rgba(82,232,176,0.3)' }}>
                  {t('cloud.publish')}
                </Button>
              </div>
              {protectionLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
              ) : latestProtectionParams.length === 0 ? (
                <div style={{ color: COLORS.muted, fontSize: 12, textAlign: 'center', padding: '20px 0' }}>{t('cloud.noData') || '暂无数据'}</div>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={protectionShowLatest ? false : { pageSize: protectionPageSize }}
                  dataSource={latestProtectionParams}
                  columns={[
                    { title: 'SN', dataIndex: 'sn', key: 'sn', width: 130, render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span> },
                    { title: t('cloud.stationName') || '站点', dataIndex: 'station_name', key: 'station_name', width: 130, render: v => v || '-' },
                    { title: t('cloud.maxChargePowerKw') || '最大充电功率', dataIndex: 'max_charge_power_kw', key: 'max_charge_power_kw', width: 130, render: v => v != null ? `${v} kW` : '-' },
                    { title: t('cloud.maxDischargePowerKw') || '最大放电功率', dataIndex: 'max_discharge_power_kw', key: 'max_discharge_power_kw', width: 130, render: v => v != null ? `${v} kW` : '-' },
                    { title: t('cloud.minSoc') || '最低SOC', dataIndex: 'min_soc', key: 'min_soc', width: 90, render: v => v != null ? `${v} %` : '-' },
                    { title: t('cloud.maxSoc') || '最高SOC', dataIndex: 'max_soc', key: 'max_soc', width: 90, render: v => v != null ? `${v} %` : '-' },
                    { title: t('cloud.gridExportLimitKw') || '电网输出限制', dataIndex: 'grid_export_limit_kw', key: 'grid_export_limit_kw', width: 130, render: v => v != null ? `${v} kW` : '-' },
                    { title: t('cloud.antiBackflowEnabled') || '防逆流', dataIndex: 'anti_backflow_enabled', key: 'anti_backflow_enabled', width: 90, render: v => <Tag color={v ? 'green' : 'default'}>{v ? t('cloud.enabled') : t('cloud.disabled')}</Tag> },
                    { title: t('cloud.updateTime') || '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160, render: v => v ? new Date(v).toLocaleString() : '-' },
                  ]}
                  style={{ background: 'transparent' }}
                  scroll={{ x: 1200 }}
                  tableLayout="fixed"
                />
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* ===== 区块B: 遥控操作 ===== */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: COLORS.solar, fontSize: 13, fontWeight: 600, marginBottom: 8, borderLeft: '3px solid #f4c542', paddingLeft: 8 }}>{t('cloud.telecontrol') || '遥控操作'}</div>
        <Row gutter={[12, 12]}>
          {/* EMS遥控 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>EMS{t('cloud.telecontrol')}</div>
                <Button type="primary" size="small" onClick={openTelecontrolModal}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.telecontrol')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.telecontrolDesc') || 'EMS控制器点位遥控：开关机、功率设定、清除告警等'}</div>
            </div>
          </Col>

          {/* 南向设备遥控 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.subTelecontrolWrite')}</div>
                <Button type="primary" size="small" onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id }); setSubTelecontrolModalOpen(true); }}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.telecontrol')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.subTelecontrolDesc') || '南向设备（PCS/BMS/空调等）点位遥控'}</div>
            </div>
          </Col>

          {/* 微网设备控制 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.deviceControl') || '微网设备控制'}</div>
                <Button type="primary" size="small" onClick={() => { setDeviceControlKey(k => k + 1); form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id }); setDeviceControlModalOpen(true); }}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.telecontrol')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.deviceControlDesc') || '微网虚拟设备控制：GridModeCtrl/PCC开合闸等'}</div>
            </div>
          </Col>

          {/* 通用控制 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.control') || '通用控制'}</div>
                <Button type="primary" size="small" onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id }); setControlModalOpen(true); }}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.telecontrol')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.controlDesc') || '通用控制命令：重启通信、恢复出厂等'}</div>
            </div>
          </Col>

          {/* 设备配置 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.deviceConfig') || '设备配置'}</div>
                <Button type="primary" size="small" onClick={() => {
                  setDeviceConfigKey(k => k + 1);
                  form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id });
                  setDeviceConfigModalOpen(true);
                }}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.telecontrol')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.deviceConfigDesc') || '设备配置下发：EMS设备拓扑配置'}</div>
            </div>
          </Col>

          {/* 通信配置 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.communicationConfig') || '通信配置'}</div>
                <Button type="primary" size="small" onClick={() => {
                  setCommunicationConfigKey(k => k + 1);
                  form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id });
                  setCommunicationConfigModalOpen(true);
                }}
                  style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>
                  {t('cloud.telecontrol')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.communicationConfigDesc') || '通信配置下发：串口/CAN参数'}</div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ===== 区块C: 召测查询 ===== */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: COLORS.storage, fontSize: 13, fontWeight: 600, marginBottom: 8, borderLeft: '3px solid #52e8b0', paddingLeft: 8 }}>{t('cloud.queryTelemetry') || '召测查询'}</div>
        <Row gutter={[12, 12]}>
          {/* 召测设备清单 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.getDeviceInfo') || '召测设备清单'}</div>
                <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id }); setGetDeviceInfoModalOpen(true); }}
                  style={{ background: 'rgba(82,232,176,0.15)', color: COLORS.storage, border: '1px solid rgba(82,232,176,0.3)' }}>
                  {t('cloud.queryTelemetry') || '查询'}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.getDeviceInfoDesc') || '召测EMS设备拓扑和清单'}</div>
            </div>
          </Col>

          {/* 召测遥测 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.getTelemetry') || '召测遥测'}</div>
                <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id, profile: 'detail' }); setGetTelemetryModalOpen(true); }}
                  style={{ background: 'rgba(82,232,176,0.15)', color: COLORS.storage, border: '1px solid rgba(82,232,176,0.3)' }}>
                  {t('cloud.queryTelemetry') || '查询'}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.getTelemetryDesc') || '召测指定设备的遥测数据'}</div>
            </div>
          </Col>

          {/* 召测告警 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.getAlarm') || '召测告警'}</div>
                <Button type="primary" size="small" icon={<BellOutlined />} onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id, status: 'active', limit: 100 }); setGetAlarmModalOpen(true); }}
                  style={{ background: 'rgba(82,232,176,0.15)', color: COLORS.storage, border: '1px solid rgba(82,232,176,0.3)' }}>
                  {t('cloud.queryTelemetry') || '查询'}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.getAlarmDesc') || '召测当前或历史告警快照'}</div>
            </div>
          </Col>

          {/* 大屏推送控制 */}
          <Col xs={24} md={12} lg={6}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{t('cloud.dashboardPush')}</div>
                <Button type="primary" size="small" icon={<DashboardOutlined />} onClick={() => { form.setFieldsValue({ device_info_id: accessibleControllers[0]?.id, enabled: true, interval_s: 10, profile: 'summary', ttl_s: 600 }); setDashboardCtrlModalOpen(true); }}
                  style={{ background: 'rgba(58,141,222,0.15)', color: COLORS.grid, border: '1px solid rgba(58,141,222,0.3)' }}>
                  {t('cloud.pushControl')}
                </Button>
              </div>
              <div style={{ color: COLORS.muted, fontSize: 11 }}>{t('cloud.dashboardCtrlDesc') || '开启/停止大屏快速推送'}</div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ===== Modals ===== */}

      {/* 策略下发 Modal */}
      <Modal
        title={t('cloud.publishStrategy')}
        open={scheduleModalOpen}
        onOk={handleSchedulePublish}
        onCancel={() => setScheduleModalOpen(false)}
        confirmLoading={schedulePublishLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true, message: t('cloud.noControllerSelected') }]}>
            <Select placeholder={t('cloud.selectController')}>
              {controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}
            </Select>
          </Form.Item>
          <Space gutter={12} style={{ marginBottom: 12 }}>
            <Form.Item name="enabled" label={t('cloud.enabled')} valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
            <Form.Item name="strategy_type" label={t('cloud.strategyType') || '策略类型'} initialValue={0} style={{ marginBottom: 0 }}>
              <Select style={{ width: 150 }}>
                <Select.Option value={0}>{t('cloud.strategyType0') || '消峰填谷'}</Select.Option>
                <Select.Option value={1}>{t('cloud.strategyType1') || '动态容量'}</Select.Option>
                <Select.Option value={2}>{t('cloud.strategyType2') || '手动'}</Select.Option>
                <Select.Option value={3}>{t('cloud.strategyType3') || '第三方'}</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="tou_loop_type" label={t('cloud.touLoopType')} initialValue={2} style={{ marginBottom: 0 }}>
              <Select style={{ width: 140 }}>
                <Select.Option value={0}>{t('cloud.touLoopType0') || '按月每天'}</Select.Option>
                <Select.Option value={1}>{t('cloud.touLoopType1') || '按周每天'}</Select.Option>
                <Select.Option value={2}>{t('cloud.touLoopType2') || '每日循环'}</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="mode" label="mode" style={{ marginBottom: 0 }}>
              <Input placeholder="可选" style={{ width: 120 }} />
            </Form.Item>
          </Space>
          <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8 }}>mode为空时自动映射strategy_type，可自定义覆盖（如：time_of_use）</div>
          <Form.Item label={t('cloud.timeSlot')} extra={t('cloud.max10Periods') || '最多10个时段'}>
            <Form.List name="schedules">
              {(fields, { add, remove }) => (
                <>
                  {/* 表头 */}
                  <Row gutter={8} style={{ marginBottom: 4 }}>
                    <Col span={5} style={{ paddingLeft: 8 }}><span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{t('cloud.start')}</span></Col>
                    <Col span={5} style={{ paddingLeft: 8 }}><span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{t('cloud.end')}</span></Col>
                    <Col span={4} style={{ paddingLeft: 8 }}><span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{t('cloud.actionType')}</span></Col>
                    <Col span={5} style={{ paddingLeft: 8 }}><span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{t('cloud.activePowerKw2') || '功率(kW)'}</span></Col>
                    <Col span={4} style={{ paddingLeft: 8 }}><span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{t('cloud.socLimit') || 'SOC限制(%)'}</span></Col>
                    <Col span={1} />
                  </Row>
                  {fields.map(({ key, name, ...rest }, index) => (
                    <Row key={key} gutter={8} style={{ marginBottom: 4 }} align="middle">
                      <Col span={5}>
                        <Form.Item {...rest} name={[name, 'StartTime']} rules={[{ required: true }]}><Input placeholder="00:00" /></Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item {...rest} name={[name, 'EndTime']} rules={[{ required: true }]}><Input placeholder="06:00" /></Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} name={[name, 'action']} rules={[{ required: true }]}>
                          <Select placeholder="类型">
                            <Select.Option value="charge">{t('cloud.charge') || '充电'}</Select.Option>
                            <Select.Option value="discharge">{t('cloud.discharge') || '放电'}</Select.Option>
                            <Select.Option value="idle">{t('cloud.idle') || '待机'}</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item {...rest} name={[name, 'Power']} rules={[{ required: true }]}><InputNumber min={0} max={10000} addonAfter="kW" placeholder="0" style={{ width: '100%' }} /></Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} name={[name, 'SocLimit']}><InputNumber min={0} max={100} addonAfter="%" placeholder="100" style={{ width: '100%' }} /></Form.Item>
                      </Col>
                      <Col span={1}>
                        {index > 0 ? <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} /> : <span />}
                      </Col>
                    </Row>
                  ))}
                  {fields.length < 10 && <Button type="dashed" onClick={() => add({ StartTime: '00:00', EndTime: '06:00', action: 'charge', Power: 100, SocLimit: 90 })} block icon={<PlusOutlined />}>{t('cloud.addTimeSlot')}</Button>}
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      {/* 智能调度 Modal */}
      <Modal
        title={t('cloud.smartDispatch') || '智能调度'}
        open={smartDispatchModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setSmartDispatchModalOpen(false)}
        confirmLoading={smartDispatchLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSmartDispatch}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="action" label={t('cloud.action') || '操作'} rules={[{ required: true }]}>
            <Select onChange={v => form.setFieldsValue({ action: v })}>
              <Select.Option value="set_enabled">{t('cloud.smartDispatchEnable') || '开关控制'}</Select.Option>
              <Select.Option value="setpoint">{t('cloud.smartDispatchSetpoint') || '功率指令'}</Select.Option>
              <Select.Option value="set_schedule">{t('cloud.smartDispatchSchedule') || '滚动计划'}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.action !== curr.action}>
            {({ getFieldValue }) => {
              const action = getFieldValue('action');
              if (action === 'set_enabled') {
                return <Form.Item name="enabled" label={t('cloud.enabled') || '启用'} valuePropName="checked"><Switch /></Form.Item>;
              }
              if (action === 'setpoint') {
                return (
                  <>
                    <Form.Item name="power_kw" label={t('cloud.powerKw')} rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} addonAfter="kW" /></Form.Item>
                    <Form.Item name="valid_from" label={t('cloud.validFrom') || '生效时间'} rules={[{ required: true }]}>
                      <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="valid_until" label={t('cloud.validUntil') || '失效时间'} rules={[{ required: true }]}>
                      <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                );
              }
              if (action === 'set_schedule') {
                return (
                  <>
                    <Form.Item name="plan_id" label="Plan ID" rules={[{ required: true }]}><Input placeholder="plan-20260714-station-001" /></Form.Item>
                    <Form.Item name="revision" label="Revision" initialValue={1} rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="interval_minutes" label="间隔(分钟)" initialValue={15} rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="valid_from" label={t('cloud.validFrom') || '生效时间'} rules={[{ required: true }]}>
                      <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="valid_until" label={t('cloud.validUntil') || '失效时间'} rules={[{ required: true }]}>
                      <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="时段列表" style={{ marginBottom: 6 }}>
                      <Form.List name="points">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...rest }, index) => (
                              <Row key={key} gutter={[6, 6]} style={{ marginBottom: 4 }} align="middle">
                                <Col span={3}>
                                  <Form.Item {...rest} name={[name, 'sequence']} initialValue={index + 1}><InputNumber min={1} style={{ width: '100%' }} size="small" /></Form.Item>
                                </Col>
                                <Col span={7}>
                                  <Form.Item {...rest} name={[name, 'starts_at']}><DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} size="small" /></Form.Item>
                                </Col>
                                <Col span={7}>
                                  <Form.Item {...rest} name={[name, 'ends_at']}><DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} size="small" /></Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item {...rest} name={[name, 'power_kw']}><InputNumber placeholder="kW" style={{ width: '100%' }} size="small" /></Form.Item>
                                </Col>
                                <Col span={3}>
                                  {index > 0 && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} size="small" />}
                                </Col>
                              </Row>
                            ))}
                            {fields.length < 20 && (
                              <Button type="dashed" onClick={() => add({ sequence: fields.length + 1, starts_at: null, ends_at: null, power_kw: null })} block icon={<PlusOutlined />} size="small">{t('cloud.addTimeSlot') || '添加时段'}</Button>
                            )}
                          </>
                        )}
                      </Form.List>
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* 保护参数 Modal */}
      <Modal
        title={t('cloud.protectionParams') || '保护参数'}
        open={protectionModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setProtectionModalOpen(false)}
        confirmLoading={protectionLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleProtectionParams}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="max_charge_power_kw" label={t('cloud.maxChargePowerKw') || '最大充电功率(kW)'}><InputNumber style={{ width: '100%' }} addonAfter="kW" /></Form.Item></Col>
            <Col span={12}><Form.Item name="max_discharge_power_kw" label={t('cloud.maxDischargePowerKw') || '最大放电功率(kW)'}><InputNumber style={{ width: '100%' }} addonAfter="kW" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="min_soc" label={t('cloud.minSoc') || '最低SOC(%)'}><InputNumber style={{ width: '100%' }} addonAfter="%" /></Form.Item></Col>
            <Col span={12}><Form.Item name="max_soc" label={t('cloud.maxSoc') || '最高SOC(%)'}><InputNumber style={{ width: '100%' }} addonAfter="%" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="grid_export_limit_kw" label={t('cloud.gridExportLimitKw') || '防逆流限制(kW)'}><InputNumber style={{ width: '100%' }} addonAfter="kW" /></Form.Item></Col>
            <Col span={12}><Form.Item name="anti_backflow_enabled" label={t('cloud.antiBackflowEnabled') || '防逆流'} valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* EMS遥控 Modal */}
      <Modal
        title={`EMS ${t('cloud.telecontrol')}`}
        open={telecontrolModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setTelecontrolModalOpen(false)}
        confirmLoading={telecontrolLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleTelecontrol}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item label={t('cloud.pointList') || '控制点位列表'} style={{ marginBottom: 8 }}>
            <Form.List name="commands">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }, index) => (
                    <Row key={key} gutter={8} style={{ marginBottom: 6 }} align="middle">
                      <Col span={10}>
                        <Form.Item {...rest} name={[name, 'point']} rules={[{ required: true }]}>
                          <Select
                            showSearch
                            allowClear
                            placeholder={t('cloud.selectOrInputPoint') || '选择或输入点位'}
                            style={{ width: '100%' }}
                          >
                            {['work_mode', 'power_set_kw', 'onOff', 'ClrAlarm', 'EnTraceLoad', 'EnProtectReverse', 'EnProtectTransf'].map(p => (
                              <Select.Option key={p} value={p}>{p}</Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...rest} name={[name, 'value']} rules={[{ required: true }]}>
                          <Input placeholder={t('cloud.pointValue') || 'value'} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        {index > 0 && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />}
                      </Col>
                    </Row>
                  ))}
                  {fields.length < 10 && (
                    <Button type="dashed" onClick={() => add({ point: '', value: '' })} block icon={<PlusOutlined />}>{t('cloud.addPoint') || '添加点位'}</Button>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      {/* 南向设备遥控 Modal */}
      <Modal
        title={t('cloud.subTelecontrolWrite')}
        open={subTelecontrolModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setSubTelecontrolModalOpen(false)}
        confirmLoading={subTelecontrolLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubTelecontrol}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="device_id" label={t('cloud.deviceNo') || '设备ID(Sub_ID)'} rules={[{ required: true, message: '请填写南向设备ID' }]}>
            <Select
              showSearch
              allowClear
              placeholder={t('cloud.subDeviceIdPlaceholder') || '选择或输入南向设备ID'}
              style={{ width: '100%' }}
            >
              {(subDevices || []).map(d => (
                <Select.Option key={d.id} value={d.global_no || d.sn}>{d.name || d.global_no || d.sn} ({d.global_no || d.sn})</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('cloud.pointList') || '控制点位列表'} style={{ marginBottom: 8 }}>
            <Form.List name="commands">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }, index) => (
                    <Row key={key} gutter={8} style={{ marginBottom: 6 }} align="middle">
                      <Col span={10}>
                        <Form.Item {...rest} name={[name, 'point']} rules={[{ required: true }]}>
                          <Select
                            showSearch
                            allowClear
                            placeholder={t('cloud.selectOrInputPoint') || '选择或输入点位'}
                            style={{ width: '100%' }}
                          >
                            {['start_stop', 'power_set_kw', 'work_mode', 'charge_discharge', 'emergency_stop'].map(p => (
                              <Select.Option key={p} value={p}>{p}</Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...rest} name={[name, 'value']} rules={[{ required: true }]}>
                          <Input placeholder={t('cloud.pointValue') || 'value'} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        {index > 0 && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />}
                      </Col>
                    </Row>
                  ))}
                  {fields.length < 10 && (
                    <Button type="dashed" onClick={() => add({ point: '', value: '' })} block icon={<PlusOutlined />}>{t('cloud.addPoint') || '添加点位'}</Button>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      {/* 微网设备控制 Modal */}
      <Modal
        title={t('cloud.deviceControl') || '微网设备控制'}
        open={deviceControlModalOpen}
        key={deviceControlKey}
        onOk={() => form.submit()}
        onCancel={() => setDeviceControlModalOpen(false)}
        confirmLoading={deviceControlLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleDeviceControl}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>

          {/* 运行控制 */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.gridModeCtrl') || '运行模式'}</div>
            <Form.Item name="gridModeCtrl"><Select allowClear placeholder="--">
              <Select.Option value={0}>{t('cloud.gridModeAuto') || '自动'}</Select.Option>
              <Select.Option value={1}>{t('cloud.gridModeShutdown') || '关机'}</Select.Option>
              <Select.Option value={2}>{t('cloud.gridModeGridOn') || '并网开机'}</Select.Option>
              <Select.Option value={3}>{t('cloud.gridModeOffGrid') || '离网开机'}</Select.Option>
            </Select></Form.Item>
          </div>

          {/* 柴发控制 */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.dgOnOffCtrlMode') || '柴发控制模式'}</div>
            <Form.Item name="dgOnOffCtrlMode"><Select allowClear placeholder="--">
              <Select.Option value={0}>{t('cloud.dgModeAuto') || '自动'}</Select.Option>
              <Select.Option value={1}>{t('cloud.dgModeManualOff') || '手动关机'}</Select.Option>
              <Select.Option value={2}>{t('cloud.dgModeManualOn') || '手动开机'}</Select.Option>
            </Select></Form.Item>
          </div>

          {/* 断路器控制 */}
          <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{t('cloud.breakerControl') || '断路器控制'}</div>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Form.Item name="pccOpenCloseCtrl" label={t('cloud.pccOpenCloseCtrl') || 'PCC'} labelCol={{ span: 10 }} wrapperCol={{ span: 14 }} style={{ marginBottom: 6 }}>
                <Select allowClear placeholder="--">
                  <Select.Option value={1}>{t('cloud.openVal') || '分闸'}</Select.Option>
                  <Select.Option value={2}>{t('cloud.closeVal') || '合闸'}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dgOpenCloseCtrl" label={t('cloud.dgOpenCloseCtrl') || '柴发'} labelCol={{ span: 10 }} wrapperCol={{ span: 14 }} style={{ marginBottom: 6 }}>
                <Select allowClear placeholder="--">
                  <Select.Option value={1}>{t('cloud.openVal') || '分闸'}</Select.Option>
                  <Select.Option value={2}>{t('cloud.closeVal') || '合闸'}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="criticalLoadOpenCloseCtrl" label={t('cloud.criticalLoadOpenCloseCtrl') || '重要负载'} labelCol={{ span: 10 }} wrapperCol={{ span: 14 }} style={{ marginBottom: 6 }}>
                <Select allowClear placeholder="--">
                  <Select.Option value={1}>{t('cloud.openVal') || '分闸'}</Select.Option>
                  <Select.Option value={2}>{t('cloud.closeVal') || '合闸'}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="normalLoadOpenCloseCtrl" label={t('cloud.normalLoadOpenCloseCtrl') || '普通负载'} labelCol={{ span: 10 }} wrapperCol={{ span: 14 }} style={{ marginBottom: 6 }}>
                <Select allowClear placeholder="--">
                  <Select.Option value={1}>{t('cloud.openVal') || '分闸'}</Select.Option>
                  <Select.Option value={2}>{t('cloud.closeVal') || '合闸'}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label={t('cloud.remark') || '备注'}>
            <Input.TextArea rows={2} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 通用控制 Modal */}
      <Modal
        title={t('cloud.control') || '通用控制'}
        open={controlModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setControlModalOpen(false)}
        confirmLoading={controlLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleControl}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="command" label={t('cloud.command') || '命令'} rules={[{ required: true }]}>
            <Select>
              <Select.Option value="restart_comm">restart_comm</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="device_id" label={t('cloud.deviceNo') || '设备ID'} tooltip="可从下拉选择，也可直接编辑">
            <Select
              showSearch
              allowClear
              placeholder={t('cloud.subDeviceIdPlaceholder') || '选择或输入设备ID'}
              filterOption={(input, option) =>
                option.children.props.children[1]?.props?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
            >
              {(subDevices || []).map(d => (
                <Select.Option key={d.id} value={d.global_no || d.sn}>
                  {d.name || d.global_no || d.sn} ({d.global_no || d.sn})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 设备配置 Modal */}
      <Modal
        title={t('cloud.deviceConfig') || '设备配置'}
        open={deviceConfigModalOpen}
        key={deviceConfigKey}
        onOk={() => form.submit()}
        onCancel={() => setDeviceConfigModalOpen(false)}
        confirmLoading={deviceConfigLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleDeviceConfig}
        >
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="operation" label="Operation" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="replace">replace</Select.Option>
              <Select.Option value="merge">merge</Select.Option>
              <Select.Option value="delete">delete</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="config_json"
            label={t('cloud.deviceConfigJson') || '配置JSON'}
            extra={<span style={{ color: COLORS.muted, fontSize: 11 }}>只需填写device_config部分，系统自动填充operation/config_format</span>}
            rules={[{ required: true, message: '请填写配置JSON' }]}
          >
            <Input.TextArea
              rows={12}
              placeholder="请填写 device_config JSON"
              style={{ color: '#fff', fontFamily: 'monospace', fontSize: 11, background: 'rgba(0,0,0,0.2)', resize: 'vertical' }}
            />
          </Form.Item>
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>示例（可复制修改）：</div>
            <pre style={{ color: 'rgba(255,255,255,0.65)', fontFamily: 'monospace', fontSize: 10, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{`{"ems":[{"ems_id":"1","ems_name":"EMS1","cabinet":[{"cab_id":"1","cab_name":"柜1","dev_class":[{"type":"PCS","devs":[{"global_no":"PCS1","no":"PCS1_1","name":"变流器","channel":"192.168.40.20:502","protocol":"modbus_tcp","extend":{"frame_interval":20,"frame_timeout":1000},"addr":"1","template":"PCS_INPOWER_100_MODBUS_SPLIT_PHASE.json"}]}]}]}]}`}</pre>
          </div>
        </Form>
      </Modal>

      {/* 通信配置 Modal */}
      <Modal
        title={t('cloud.communicationConfig') || '通信配置'}
        open={communicationConfigModalOpen}
        key={communicationConfigKey}
        onOk={() => form.submit()}
        onCancel={() => setCommunicationConfigModalOpen(false)}
        confirmLoading={communicationConfigLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCommunicationConfig}
          initialValues={{
            operation: 'replace',
            config_json: JSON.stringify({
              com_cfg: [
                { port: 'RS485_1', baudrate: 9600, databit: 8, parity: 'N', stopbit: 1 }
              ],
              can_cfg: [
                { port: 'can1', bitrate: 500000, disable: false, filter_en: false, filters: [] }
              ]
            }, null, 2)
          }}
        >
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="operation" label="Operation" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="replace">replace</Select.Option>
              <Select.Option value="merge">merge</Select.Option>
              <Select.Option value="delete">delete</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="config_json"
            label={t('cloud.communicationConfigJson') || '配置JSON'}
            extra={<span style={{ color: COLORS.muted, fontSize: 11 }}>只需填写communication_config部分，系统自动填充operation/config_format</span>}
            rules={[{ required: true, message: '请填写配置JSON' }]}
          >
            <Input.TextArea
              rows={12}
              style={{ color: '#fff', fontFamily: 'monospace', fontSize: 11, background: 'rgba(0,0,0,0.2)', resize: 'vertical' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 召测设备清单 Modal */}
      <Modal
        title={t('cloud.getDeviceInfo') || '召测设备清单'}
        open={getDeviceInfoModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setGetDeviceInfoModalOpen(false)}
        confirmLoading={getDeviceInfoLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={400}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleGetDeviceInfo}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Alert message="下发后将通过EMS的command_ack返回设备清单" type="info" showIcon />
        </Form>
      </Modal>

      {/* 召测遥测 Modal */}
      <Modal
        title={t('cloud.getTelemetry') || '召测遥测'}
        open={getTelemetryModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setGetTelemetryModalOpen(false)}
        confirmLoading={getTelemetryLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleGetTelemetry}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="profile" label="profile">
            <Select>
              <Select.Option value="summary">summary</Select.Option>
              <Select.Option value="detail">detail</Select.Option>
              <Select.Option value="debug">debug</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="device_ids" label={t('cloud.deviceIds') || '设备ID列表'} tooltip="逗号分隔，不填则查所有设备">
            <Input placeholder="PCS1, BMS1" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 召测告警 Modal */}
      <Modal
        title={t('cloud.getAlarm') || '召测告警'}
        open={getAlarmModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setGetAlarmModalOpen(false)}
        confirmLoading={getAlarmLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={400}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleGetAlarm}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="status" label="status">
            <Select>
              <Select.Option value="active">active</Select.Option>
              <Select.Option value="cleared">cleared</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="limit" label={t('cloud.limit') || '数量上限'}>
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 大屏推送控制 Modal */}
      <Modal
        title={t('cloud.dashboardPush')}
        open={dashboardCtrlModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setDashboardCtrlModalOpen(false)}
        confirmLoading={dashboardCtrlLoading}
        okText={t('cloud.publish')}
        cancelText={t('common.cancel')}
        width={400}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleDashboardCtrl}>
          <Form.Item name="device_info_id" label={t('cloud.selectController')} rules={[{ required: true }]}>
            <Select>{controllers.map(c => <Select.Option key={c.id} value={c.id}>{c.station_name || c.sn} ({c.sn})</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="enabled" label={t('cloud.enabled') || '启用'} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="interval_s" label={t('cloud.intervalS') || '间隔(秒)'}><InputNumber min={1} max={3600} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="ttl_s" label="TTL(秒)"><InputNumber min={60} max={86400} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="profile" label="profile">
            <Select>
              <Select.Option value="summary">summary</Select.Option>
              <Select.Option value="detail">detail</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TelecontrolTab;
